import React from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import Loading from '../../../../components/loading';
import { useGeneralEvolutionRectifications } from '../hooks/useGeneralEvolutionRectifications';

const EMPTY = '—';

// Só os campos que mudaram entre o antes e o depois de cada retificação.
function changesOf({ previous, current }) {
  const fields = [
    { label: 'Data', from: previous.date || EMPTY, to: current.date || EMPTY },
    { label: 'Descrição', from: previous.description || EMPTY, to: current.description || EMPTY },
  ];
  return fields.filter((field) => field.from !== field.to);
}

export default function GeneralEvolutionRectificationHistoryModal({ open, onClose, evolution }) {
  const { rectifications, isLoading } = useGeneralEvolutionRectifications(
    open ? evolution?.id : null,
  );

  return (
    <Dialog
      data-cy="general-evolution-rectification-history-modal"
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <Loading isToHidden={isLoading === false} />
      <DialogTitle>
        <Typography sx={{ fontWeight: 'bold' }}>Retificações da Evolução</Typography>
        <Typography variant="body2" color="text.secondary">
          {evolution?.data}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {rectifications.length > 0 ? (
          <Timeline sx={{ mt: 1, [`& .MuiTimelineItem-root::before`]: { flex: 0, padding: 0 } }}>
            {rectifications.map((rectification, index) => (
              <TimelineItem key={rectification.id}>
                <TimelineSeparator>
                  <TimelineDot color="warning" variant="outlined" />
                  {index < rectifications.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent sx={{ py: '12px', px: 2 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {dayjs(rectification.rectifiedAt).format('DD/MM/YYYY HH:mm')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    por {rectification.rectifiedBy}
                  </Typography>
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {changesOf(rectification).map((change) => (
                      <Box key={change.label} data-cy="general-evolution-rectification-change">
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {change.label}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ whiteSpace: 'pre-line', textDecoration: 'line-through' }}
                        >
                          {change.from}
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                          {change.to}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        ) : (
          !isLoading && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                Nenhuma retificação registrada para esta evolução.
              </Typography>
            </Box>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}

GeneralEvolutionRectificationHistoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  evolution: PropTypes.shape({
    id: PropTypes.number.isRequired,
    data: PropTypes.string,
  }),
};

GeneralEvolutionRectificationHistoryModal.defaultProps = {
  evolution: null,
};
